import { Content, Item, ItemText, Portal, Root, Trigger, Viewport } from '@radix-ui/react-select';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import { formatDistance } from 'date-fns';
import { type FunctionComponent, useMemo } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Card } from '~/ui/core/card/Card';
import { IconHistory } from '~/ui/core/icons';

import type { Thread } from '../../data-contract/chat';

type ThreadDropdownProps = {
    readonly threads: readonly Thread[];
    readonly currentThreadId: string | null;
    readonly onSelect: (id: string) => void;
};

export const ThreadDropdown: FunctionComponent<ThreadDropdownProps> = ({ threads, currentThreadId, onSelect }) => {
    const translate = useTranslate();
    const { applicationState } = useApplicationContext();

    const sortedThreads = useMemo<readonly Thread[]>(
        () => [...threads].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
        [threads],
    );

    const handleValueChange = (value: string): void => {
        onSelect(value);
    };

    return (
        <Root value={currentThreadId ?? undefined} onValueChange={handleValueChange}>
            <Trigger asChild={true} disabled={threads.length === 0}>
                <Button
                    aria-label={translate('chat.history')}
                    data-testid="thread-dropdown"
                    iconOnly={true}
                    size="sm"
                    variant="secondary"
                >
                    <IconHistory />
                </Button>
            </Trigger>
            <Portal>
                <Content
                    className="z-99999 w-[18rem] overflow-hidden rounded-xl bg-gray-100 font-sans shadow-md"
                    position="popper"
                    sideOffset={8}
                    style={{ maxHeight: 'var(--radix-select-content-available-height)' }}
                >
                    <Viewport className="bg-white-200 p-xs">
                        {sortedThreads.map((thread) => (
                            <Item key={thread.id} asChild={true} value={thread.id}>
                                <Card
                                    className={clsx(
                                        'mb-xs box-border w-full last:mb-0',
                                        'data-highlighted:outline data-highlighted:outline-orange-700',
                                    )}
                                    isSelected={thread.id === currentThreadId}
                                >
                                    <div className="text-left text-sm font-medium leading-[1.25rem] text-black-700">
                                        <ItemText>
                                            {is.nonEmptyString(thread.name)
                                                ? thread.name
                                                : translate('chat.untitled-thread')}
                                        </ItemText>
                                    </div>
                                    <div className="text-xxs font-normal leading-[1.25rem] text-gray-700">
                                        {formatDistance(new Date(thread.updatedAt), new Date(), {
                                            addSuffix: true,
                                            locale: applicationState.language.date.locale,
                                        })}
                                    </div>
                                </Card>
                            </Item>
                        ))}
                    </Viewport>
                </Content>
            </Portal>
        </Root>
    );
};
